/**
 * spaceState.ts
 * Mutable singleton state for the World 2 space shoot-em-up scene.
 *
 * Sits parallel to the platformer `gameState` rather than inside it, so the
 * shmup engine never imports Nova / level / collision concerns it does not
 * need. SpaceScene owns the lifecycle (init / reset / destroy).
 *
 * DECISION: This module holds DATA only — it does not construct entities. The
 * scene owns construction (see SpaceScene.init). Keeping the import of
 * ShipEntity type-only avoids a runtime cycle between ShipEntity (which reads
 * `spaceState.cameraX` from its `update()`) and spaceState.
 *
 * NO React imports, NO Reanimated imports, NO async — engine-internal only.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceState
 */

import type { ShipEntity } from '../entities/ShipEntity';
import type { ShipProjectile } from '../entities/ShipProjectile';
import type { ShipMissile } from '../entities/ShipMissile';
import type { AsteroidEntity } from '../entities/AsteroidEntity';
import type { SpaceEnemyBase } from '../entities/SpaceEnemyBase';
import type { VoidMothership } from '../entities/VoidMothership';
import type { BossProjectile } from '../entities/BossProjectile';
import type { BossMissile } from '../entities/BossMissile';
import type { FighterDroneLaser } from '../entities/FighterDroneLaser';
import type { ClusterBomb } from '../entities/ClusterBomb';
import type { BombShrapnel } from '../entities/BombShrapnel';
import type { HomingMine } from '../entities/HomingMine';
import type { SpaceShard } from '../entities/SpaceShard';
import type { SpaceStarSeed } from '../entities/SpaceStarSeed';
import { SPACE_LEVEL_DEFAULT, type SpaceLevelConfig } from '../levels/spaceLevels';
import { resetSpaceHudSnapshot } from './spaceHudSnapshot';

/**
 * Module-level singleton state for the active space scene.
 * Written by `spaceStep`; read by rendering and HUD layers.
 */
export interface SpaceState {
  /** The player's ship, or null when no space scene is active. */
  ship: ShipEntity | null;
  /** World-space X of the camera's left edge (auto-scrolls right each tick). */
  cameraX: number;
  /** True while the space scene is running (mirror of platformer `isRunning`). */
  isRunning: boolean;
  /** Total elapsed seconds in the current space-level session. */
  elapsedSeconds: number;
  /**
   * Active ship-fired projectiles. Renderer reads, `shipCombat`
   * writes. Pool-managed: NOT cleared by `initSpaceState()` or
   * `clearSpaceState()` — `resetShipCombat()` owns the lifecycle.
   */
  projectiles: ShipProjectile[];
  /**
   * Seconds remaining on the ship's primary-fire cooldown.
   * Pool-managed by `resetShipCombat()` (see `projectiles`).
   */
  attackCooldown: number;
  /**
   * Active ship-fired missiles (secondary weapon). Renderer reads,
   * `shipCombat` writes. Pool-managed: NOT cleared by
   * `initSpaceState()` or `clearSpaceState()` — `resetShipCombat()`
   * owns the lifecycle, same as `projectiles`.
   */
  missiles: ShipMissile[];
  /**
   * Seconds remaining on the ship's secondary-fire (missile) cooldown.
   * Pool-managed by `resetShipCombat()` (see `missiles`).
   */
  missileCooldown: number;
  /**
   * Seconds the Attack input has been held continuously without
   * release. Drives the secondary-missile fire gate — when this
   * crosses `SHIP_MISSILE_HOLD_THRESHOLD_S` while `attackHeld` is true
   * and the player has not yet fired a missile during this hold, one
   * missile launches. Pool-managed by `resetShipCombat()` (see
   * `missiles`).
   */
  attackHoldDuration: number;
  /**
   * True once a missile has been fired during the current Attack hold.
   * Cleared back to false when the player releases Attack. Guards
   * against re-firing while the player keeps holding past the
   * threshold — one missile per hold gesture. Pool-managed by
   * `resetShipCombat()` (see `missiles`).
   */
  missileFiredThisHold: boolean;
  /** Camera-shake X offset in logical px (applied by the renderer). */
  shakeX: number;
  /** Camera-shake Y offset in logical px (applied by the renderer). */
  shakeY: number;
  /**
   * Current shake intensity in logical px. Each tick the renderer
   * draws the world offset by a random vector inside the box
   * `[-shakeMagnitude, +shakeMagnitude]` on each axis. Decays toward
   * zero linearly over `shakeDurationPeak`.
   */
  shakeMagnitude: number;
  /** Seconds remaining before the active shake event fully decays. */
  shakeTimer: number;
  /**
   * Duration at the moment the active shake event was triggered (s).
   * Used purely to compute the linear-decay ratio inside
   * `tickCameraShake`; not exposed to renderers.
   */
  shakeDurationPeak: number;
  /** Peak magnitude of the active shake event (px). Source for the decay ratio. */
  shakePeakMagnitude: number;
  /** Active asteroid obstacles. Renderer reads, asteroidSystem writes. */
  asteroids: AsteroidEntity[];
  /** Active hostile space enemies (fighter drones, etc). Renderer reads, spaceEnemySystem writes. */
  enemies: SpaceEnemyBase[];
  /** The current World 2 boss, or null until triggered / after defeat. */
  boss: VoidMothership | null;
  /**
   * Active boss-fired turret projectiles. Renderer reads, `bossSystem`
   * writes. Always travel LEFT (mirror of `projectiles` which travel
   * right) so the two arrays never need to disambiguate by direction.
   */
  bossProjectiles: BossProjectile[];
  /**
   * Active boss-fired homing missiles. Renderer reads, `bossSystem`
   * (via `bossMissileBarrage`) writes. Kept on its own array so the
   * missile pool, cull rules, and TTL are isolated from the turret
   * bolts which share neither.
   */
  bossMissiles: BossMissile[];
  /**
   * Active `FighterDrone`-fired laser bolts. Renderer reads,
   * `fighterDroneFire` writes. Pool-managed: NOT cleared by
   * `initSpaceState()` or `clearSpaceState()` —
   * `releaseAllFighterDroneLasers()` owns the lifecycle (the pool
   * lives in `fighterDroneFire`). Kept on its own array (rather than
   * folded into `bossProjectiles`) so the fighter weapon pool stays
   * isolated from boss turret bolts.
   */
  fighterLasers: FighterDroneLaser[];
  /**
   * Active `BomberDrone`-dropped cluster bombs (parent payload) and
   * their post-fuse shrapnel fragments. Renderer reads, `bomberDroneDrop`
   * writes. Pool-managed pair: NOT cleared by `initSpaceState()` or
   * `clearSpaceState()` — `releaseAllBomberOrdnance()` owns the
   * lifecycle (both pools live in `bomberDroneDrop`). Kept on their
   * own arrays (rather than folded into `fighterLasers` or
   * `bossProjectiles`) so the bomber ordnance pool stays isolated
   * from every other bolt stream.
   */
  clusterBombs: ClusterBomb[];
  bombShrapnel: BombShrapnel[];
  /**
   * Active Level 2-3 homing mines. Renderer reads, `homingMineSpawner`
   * writes. Empty on every level except `'2-3'` — the spawner self-
   * clears on level transitions so a mid-scene config swap can't leak
   * mines into a non-nebula level.
   */
  mines: HomingMine[];
  /**
   * Active Constellation Shards. Renderer reads, `spaceShardSystem`
   * writes. Spawned once on level entry from `levelConfig.shards`;
   * collected on ship contact (id appended to a module-local
   * per-session list inside `spaceShardSystem`).
   */
  shards: SpaceShard[];
  /**
   * Active enemy-drop loot pickups (Star Seeds). Renderer reads,
   * `spaceLootSystem` writes. Spawned probabilistically on enemy
   * kills; collected on ship contact.
   */
  loot: SpaceStarSeed[];
  /**
   * The level config the scene was initialised with. Drives spawner
   * gating + the end-condition watcher in `stepSpace`. Defaults to
   * `SPACE_LEVEL_DEFAULT` (everything enabled, never ends) so legacy
   * call sites that never passed a config keep their old behaviour.
   */
  levelConfig: SpaceLevelConfig;
  /**
   * Set true by `stepSpace` once the active level's end condition fires.
   * Read by SpaceScene's outer driver (and tests) to decide when to
   * transition out. The step function itself keeps ticking after this
   * flips so the level fade-out / score-tally screen can still render
   * a couple of frames without an extra pause flag.
   */
  levelComplete: boolean;
  /**
   * Outro grace period (seconds remaining) between meeting the
   * end-condition and flipping `levelComplete`. `null` while the
   * level is still in progress, set to `SPACE_LEVEL_OUTRO_GRACE_S`
   * by `stepSpace` the tick the condition first fires, ticked
   * down each subsequent tick until `<= 0`. Gives the screen a
   * beat to settle before the Sector Cleared overlay lands
   * (W2 overhaul: user feedback that levels "end all of a sudden").
   */
  outroTimer: number | null;
  /**
   * Set true by `shipDamage.resolveShipHazardCollisions` the tick the
   * ship's HP reaches zero. Read by `app/space-game.tsx` to mount the
   * Game Over overlay. `stepSpace` skips its damage pass while this is
   * true so the dead-ship tick can't keep accumulating hits in the
   * couple of frames before the React layer freezes the canvas.
   */
  gameOver: boolean;
}

/** The single mutable space-scene state instance. */
export const spaceState: SpaceState = {
  ship: null,
  cameraX: 0,
  isRunning: false,
  elapsedSeconds: 0,
  projectiles: [],
  attackCooldown: 0,
  missiles: [],
  missileCooldown: 0,
  attackHoldDuration: 0,
  missileFiredThisHold: false,
  shakeX: 0,
  shakeY: 0,
  shakeMagnitude: 0,
  shakeTimer: 0,
  shakeDurationPeak: 0,
  shakePeakMagnitude: 0,
  asteroids: [],
  enemies: [],
  boss: null,
  bossProjectiles: [],
  bossMissiles: [],
  fighterLasers: [],
  clusterBombs: [],
  bombShrapnel: [],
  mines: [],
  shards: [],
  loot: [],
  levelConfig: SPACE_LEVEL_DEFAULT,
  levelComplete: false,
  outroTimer: null,
  gameOver: false,
};

/**
 * Rewind the scene-level scalars (camera + clock + running flag) and mark the
 * scene running. SpaceScene assigns `spaceState.ship` itself after calling
 * this so this module never imports the entity at runtime.
 *
 * Note: does NOT touch pool-managed fields. Each pool owns its own reset
 * helper — `resetShipCombat()`, `resetAsteroids()`, `resetSpaceEnemies()`,
 * `resetBoss()`, `releaseAllFighterDroneLasers()`,
 * `releaseAllBomberOrdnance()`, `resetHomingMines()`,
 * `resetSpaceShards()`. SpaceScene calls all of them alongside this.
 */
export function initSpaceState(): void {
  spaceState.cameraX = 0;
  spaceState.isRunning = true;
  spaceState.elapsedSeconds = 0;
  spaceState.levelComplete = false;
  spaceState.outroTimer = null;
  spaceState.gameOver = false;
  spaceState.shakeX = 0;
  spaceState.shakeY = 0;
  spaceState.shakeMagnitude = 0;
  spaceState.shakeTimer = 0;
  spaceState.shakeDurationPeak = 0;
  spaceState.shakePeakMagnitude = 0;
  // DECISION: Reset the HUD snapshot alongside the engine state so
  // SpaceHUD never paints the previous session's hearts/name on its
  // first read before stepSpace writes the new tick (PR #124 review).
  resetSpaceHudSnapshot();
}

/**
 * Tear down the space-scene singleton. Drops the ship reference and
 * pauses the scene. Like `initSpaceState`, this does not touch pool-
 * managed fields — each pool's `reset*` / `releaseAll*` helper owns
 * its array (see `initSpaceState` doc). SpaceScene sequences them all.
 */
export function clearSpaceState(): void {
  spaceState.ship = null;
  spaceState.cameraX = 0;
  spaceState.isRunning = false;
  spaceState.elapsedSeconds = 0;
  spaceState.levelConfig = SPACE_LEVEL_DEFAULT;
  spaceState.levelComplete = false;
  spaceState.outroTimer = null;
  spaceState.gameOver = false;
  spaceState.shakeX = 0;
  spaceState.shakeY = 0;
  spaceState.shakeMagnitude = 0;
  spaceState.shakeTimer = 0;
  spaceState.shakeDurationPeak = 0;
  spaceState.shakePeakMagnitude = 0;
  resetSpaceHudSnapshot();
}
